import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../services/api';
import { AlertTriangle, Loader2 } from 'lucide-react';

const CONFIRM_WORD = 'delete';
const POLL_INTERVAL_MS = 2000;
// ~5 minutes. A purge that has not reported a terminal status by then is not
// coming back on its own — stop polling and tell the admin to check the audit.
const MAX_POLL_ATTEMPTS = 150;
// Every status the server will not move off on its own. `completed_with_errors`
// means the database teardown succeeded in full but a post-commit external
// effect (a Stripe call, an ActiveStorage purge) did not.
const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed'];

const formatCredits = (value) =>
    typeof value === 'number' ? value.toLocaleString() : value;

// Irreversible: purges the billing account, its workspaces and every row beneath
// them. The typed word is a speed bump only — the server preflight is the real gate.
const DeleteAccountModal = ({ account, onClose, onDeleted }) => {
    const [preview, setPreview] = useState(null);
    const [typed, setTyped] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState('');
    const [force, setForce] = useState(false);
    // Populated only on `completed_with_errors`: the DB teardown succeeded but
    // one or more post-commit vendor calls did not.
    const [externalErrors, setExternalErrors] = useState([]);
    const pollRef = useRef(null);
    const attemptsRef = useRef(0);

    useEffect(() => {
        adminAPI.getPurgePreview(account.id)
            .then(setPreview)
            .catch(() => setError('Could not load the account preview.'));
        return () => clearInterval(pollRef.current);
    }, [account.id]);

    const violations = preview?.violations || [];
    // The server designs some violations (an active paid subscription) as
    // overridable via `force`. An override is offered only when EVERY violation
    // is overridable — one non-overridable block is absolute.
    const overridable = violations.length > 0 && violations.every((v) => v.overridable);
    const blocked = violations.length > 0 && !(overridable && force);
    const canConfirm = typed === CONFIRM_WORD && !blocked && !submitting && preview;
    const completedWithErrors = status === 'completed_with_errors';

    const handleDelete = async () => {
        setSubmitting(true);
        setError('');
        try {
            const { purge_audit_id: auditId } = await adminAPI.deleteBillingAccount(account.id, {
                force: overridable && force,
            });
            setStatus('pending');
            attemptsRef.current = 0;
            pollRef.current = setInterval(async () => {
                attemptsRef.current += 1;
                if (attemptsRef.current > MAX_POLL_ATTEMPTS) {
                    clearInterval(pollRef.current);
                    setError('Timed out waiting for the purge to report back. It may still be running — check the purge audit log before retrying.');
                    setSubmitting(false);
                    return;
                }
                try {
                    const next = await adminAPI.getPurgeStatus(auditId);
                    setStatus(next.status);
                    // `completed_with_errors` is terminal too. The account is
                    // gone from our side, but a Stripe call failed — so the
                    // subscription may still be live. Stop polling, but do NOT
                    // close: the admin has to see which calls failed and
                    // dismiss this deliberately.
                    if (TERMINAL_STATUSES.includes(next.status)) {
                        clearInterval(pollRef.current);
                        if (next.status === 'completed') {
                            onDeleted();
                        } else if (next.status === 'completed_with_errors') {
                            setExternalErrors(next.external_errors || []);
                        } else {
                            setError(next.error_message || 'The purge failed.');
                        }
                        setSubmitting(false);
                    }
                } catch {
                    // A transient status read must not become an unhandled
                    // rejection that leaves the interval running forever.
                    // Keep polling; the attempt cap is the backstop.
                }
            }, POLL_INTERVAL_MS);
        } catch (err) {
            // The 409 body is { error: 'purge_blocked', violations: [...] }.
            // `error` is a machine code — show the human messages instead.
            const blockers = (err?.response?.data?.violations || [])
                .map((v) => v.message)
                .filter(Boolean);
            setError(
                blockers.length
                    ? blockers.join(' ')
                    : 'The delete request was refused by the server.'
            );
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg bg-card border border-border p-5 shadow-lg">
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">Delete account</h2>
                        <p className="text-sm text-muted-foreground">
                            Permanently removes {account.owner?.email} and everything under the account.
                            This cannot be undone.
                        </p>
                    </div>
                </div>

                {!preview && !error && (
                    <p className="text-sm text-muted-foreground">Loading preview…</p>
                )}

                {/* Once the teardown has run this is history, not a forecast. */}
                {preview && !completedWithErrors && (
                    <div className="mb-4 rounded-md border border-border bg-muted/40 p-3">
                        <div className="text-xs font-medium uppercase text-muted-foreground mb-2">
                            Will be destroyed
                        </div>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-foreground">
                            {Object.entries(preview.counts || {}).map(([key, value]) => (
                                <li key={key} className="flex justify-between">
                                    <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                                    <span className="font-semibold">{value}</span>
                                </li>
                            ))}
                        </ul>
                        {/* Not a count, but an admin must see they are about to
                            destroy a credit balance and a live subscription. */}
                        <dl className="mt-3 border-t border-border pt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-foreground">
                            <div className="flex justify-between">
                                <dt>Credit balance</dt>
                                <dd className="font-semibold">{formatCredits(preview.total_balance ?? 0)}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt>Subscription</dt>
                                <dd className="font-semibold">{preview.subscription_status || 'none'}</dd>
                            </div>
                        </dl>
                    </div>
                )}

                {violations.map((v) => (
                    <div key={v.code} className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {v.message}
                    </div>
                ))}

                {error && (
                    <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {error}
                    </div>
                )}

                {status && !TERMINAL_STATUSES.includes(status) && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Purge {status}…
                    </div>
                )}

                {/* The teardown succeeded; a vendor call did not. Naming the
                    failed effects is the whole point — otherwise the admin has
                    no idea whether the subscription is still billing. */}
                {completedWithErrors && (
                    <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                        <div className="font-semibold mb-1">
                            Account deleted, but {externalErrors.length} external call
                            {externalErrors.length === 1 ? '' : 's'} failed
                        </div>
                        <p className="mb-2 text-destructive/90">
                            Everything in our database is gone. The following ran after the
                            teardown and did not succeed — check Stripe manually before
                            considering this account closed.
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            {externalErrors.map((e, i) => (
                                <li key={`${e.effect}-${i}`}>
                                    <span className="font-mono text-xs">{e.effect}</span>
                                    {e.error ? ` — ${e.error}` : ''}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {overridable && (
                    <label className="mb-3 flex items-start gap-2 text-sm text-foreground">
                        <input
                            type="checkbox"
                            checked={force}
                            onChange={(e) => setForce(e.target.checked)}
                            disabled={submitting}
                            className="mt-0.5 rounded border-input"
                        />
                        <span>
                            Purge anyway — override the block{violations.length > 1 ? 's' : ''} above
                        </span>
                    </label>
                )}

                {/* The account is already gone — re-confirming a delete makes no
                    sense. The only action left is an explicit acknowledgement. */}
                {completedWithErrors ? (
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onDeleted}
                            className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
                        >
                            Dismiss
                        </button>
                    </div>
                ) : (
                <>
                <label htmlFor="delete-confirm-input" className="block text-sm font-medium text-foreground mb-1">
                    Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span> to confirm
                </label>
                <input
                    id="delete-confirm-input"
                    type="text"
                    value={typed}
                    placeholder={CONFIRM_WORD}
                    onChange={(e) => setTyped(e.target.value)}
                    disabled={blocked || submitting}
                    className="mb-4 block w-full rounded-md border-input text-sm focus:border-ring focus:ring-ring"
                />

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="rounded-md border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={!canConfirm}
                        className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Delete account permanently
                    </button>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default DeleteAccountModal;
